import { get, post, resolveApiUrl } from './api'

export type PptTaskStatus =
  | 'draft'
  | 'queued'
  | 'running'
  | 'outline_ready'
  | 'sample_ready'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'expired'

export interface CreatePptTaskRequest {
  prompt: string
  mode?: string
  title?: string
  preflightTicket?: { payload: unknown; signature: string }
}

export interface PreflightFile {
  url: string
  storagePath: string
  name: string
  mimeType: string
  size: number
}

export interface ImageToPptPreflightResult {
  totalPages: number
  costPoints: number
  retailYuanPerPage: number
  preflightTicket: { payload: unknown; signature: string }
}

export interface QuotaPaywall {
  currentPoints: number
  requiredPoints: number
  retailYuanPerPage: number
}

export class QuotaRequiredError extends Error {
  paywall: QuotaPaywall

  constructor(message: string, paywall: QuotaPaywall) {
    super(message)
    this.name = 'QuotaRequiredError'
    this.paywall = paywall
  }
}

export interface PptTaskRecord {
  id: string
  user_id: string
  mode: string
  status: PptTaskStatus
  title: string
  prompt: string
  input_files: string[]
  settings: Record<string, unknown>
  cost_points: number
  points_charged: boolean
  current_stage: string
  progress: number
  outline?: unknown
  retry_count?: number
  max_retries?: number
  worker_attempts?: number
  max_worker_attempts?: number
  error_public?: string | null
  error_admin?: string | null
  created_at: string
  updated_at: string
  completed_at?: string | null
  conversation_id?: string | null
  message_id?: string | null
  project_id?: string | null
  task_kind?: string
  executed_provider?: 'official' | 'third_party' | null
  total_pages?: number
  charged_pages?: number
  refunded_pages?: number
  quality_failure_summary?: Array<{ page_index: number; reason: string }>
}

export interface PptTaskArtifact {
  id: string
  task_id: string
  artifact_type?: string
  type?: string
  file_id?: string | null
  url?: string | null
  metadata?: Record<string, unknown>
  created_at: string
}

export interface PptTaskEvent {
  id: string
  task_id: string
  event_type: string
  message: string
  details?: Record<string, unknown>
  level: 'info' | 'warn' | 'error'
  created_at: string
}

export interface PptTaskDetail {
  task: PptTaskRecord
  artifacts: PptTaskArtifact[]
  latestEvent: PptTaskEvent | null
}

export function preflightImageToPpt(files: PreflightFile[]): Promise<ImageToPptPreflightResult> {
  return post<ImageToPptPreflightResult>('/uploads/preflight', { files })
}

export async function createPptTask(request: CreatePptTaskRequest): Promise<PptTaskRecord> {
  const data = await post<{ task: PptTaskRecord }>('/tasks', request)
  if (!data.task) throw new Error('创建任务失败：返回数据缺少 task')
  return data.task
}

export async function listPptTasks(): Promise<PptTaskRecord[]> {
  const data = await get<{ tasks: PptTaskRecord[] }>('/tasks')
  return data.tasks || []
}

export async function getPptTask(id: string): Promise<PptTaskDetail> {
  const data = await get<{ task: PptTaskRecord; artifacts: PptTaskArtifact[]; latestEvent: PptTaskEvent | null }>(
    `/tasks/${id}`,
  )
  if (!data.task) throw new Error('任务不存在')
  return {
    task: data.task,
    artifacts: data.artifacts || [],
    latestEvent: data.latestEvent || null,
  }
}


// ---------------------------------------------------------------------------
// Coupon (兑换码) API — endpoints use raw JSON response
// ---------------------------------------------------------------------------

function tokenFromStorage(): string | null {
  return localStorage.getItem('ppt_token')
}

export async function redeemCouponCode(code: string): Promise<{ grantedPoints: number; balance: number }> {
  const token = tokenFromStorage()
  const res = await fetch(resolveApiUrl('/coupons/redeem'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ code }),
  })
  const json = await res.json()
  if (!json.success) throw new Error(json.detail || '兑换失败')
  return { grantedPoints: json.grantedPoints || 0, balance: json.balance || 0 }
}

